import type { BxpWebOperationsMethods } from '@/libraries/bxp-web-stargate-client/src/hook/bxp-web-operations-client.types';
import { BxpWebOperationsError } from '@/libraries/bxp-web-stargate-client/src/hook/bxp-web-operations-error';

import type {
	JsmIncidentManagementIntent,
	JsmIncidentManagementSetupState,
} from './jsm-incident-management-setup-state';

const INCIDENT_TEAM_NAME = 'My Operations Team';
const MEMBERSHIP_CHECKS = 5;
const STATUS_CHECKS = 20;
const CHECK_INTERVAL_MS = 3_000;

export type IncidentSetupFailureReason =
	| 'scope-mismatch'
	| 'gate-disabled'
	| 'client-unavailable'
	| 'membership-pending'
	| 'permission'
	| 'enable-failed'
	| 'timeout'
	| 'interrupted'
	| 'api';

export class JsmIncidentManagementSetupError extends Error {
	public constructor(
		public readonly reason: IncidentSetupFailureReason,
		public readonly apiError?: BxpWebOperationsError,
	) {
		super(reason);
		this.name = 'JsmIncidentManagementSetupError';
	}
}

type RunIncidentSetupOptions = {
	readonly client: Partial<BxpWebOperationsMethods>;
	readonly intent: JsmIncidentManagementIntent;
	readonly cloudId: string;
	readonly signal: AbortSignal;
	readonly isGateEnabled: () => boolean;
	readonly wait: (delay: number) => Promise<void>;
	readonly onState: (state: JsmIncidentManagementSetupState) => void;
};

const bindOperationsMethods = (client: Partial<BxpWebOperationsMethods>) => {
	const createTeam = client.createOperationsTeam?.bind(client);
	const getOwningOrg = client.getOperationsOwningOrg?.bind(client);
	const getMembership = client.getOperationsTeamMembership?.bind(client);
	const getEligibility = client.getOperationsEligibility?.bind(client);
	const enableTeam = client.enableOperations?.bind(client);
	const getRequestStatus = client.getOperationsRequestStatus?.bind(client);
	if (
		!createTeam ||
		!getOwningOrg ||
		!getMembership ||
		!getEligibility ||
		!enableTeam ||
		!getRequestStatus
	) {
		throw new JsmIncidentManagementSetupError('client-unavailable');
	}
	return { createTeam, getOwningOrg, getMembership, getEligibility, enableTeam, getRequestStatus };
};

/** One mounted attempt. Mutations are never retried and progress is not restored after reload. */
export const runJsmIncidentManagementSetup = async ({
	client,
	intent,
	cloudId,
	signal,
	isGateEnabled,
	wait,
	onState,
}: RunIncidentSetupOptions): Promise<void> => {
	const { createTeam, getOwningOrg, getMembership, getEligibility, enableTeam, getRequestStatus } =
		bindOperationsMethods(client);
	const request = { cloudId, signal };
	const checkActive = () => {
		if (signal.aborted) throw new DOMException('Setup stopped', 'AbortError');
	};
	const checkGate = () => {
		checkActive();
		let enabled = false;
		try {
			enabled = isGateEnabled();
		} catch {
			/* Unavailable gates block new mutations. */
		}
		if (!enabled) throw new JsmIncidentManagementSetupError('gate-disabled');
	};
	const confirmMembership = async (teamId: string) => {
		for (let index = 0; index < MEMBERSHIP_CHECKS; index++) {
			checkActive();
			const membership = await getMembership({ ...request, teamId, accountId: intent.accountId });
			if (membership.creatorFullMember) return;
			if (index < MEMBERSHIP_CHECKS - 1) await wait(CHECK_INTERVAL_MS);
		}
		throw new JsmIncidentManagementSetupError('membership-pending');
	};
	const poll = async (teamId: string, asyncRequestId: string) => {
		for (let index = 0; index < STATUS_CHECKS; index++) {
			checkActive();
			const result = await getRequestStatus({ ...request, asyncRequestId });
			checkActive();
			if (result.status === 'successful') {
				onState({ stage: 'complete', teamId, asyncRequestId });
				return;
			}
			if (result.status === 'failed') throw new JsmIncidentManagementSetupError('enable-failed');
			if (index < STATUS_CHECKS - 1) await wait(CHECK_INTERVAL_MS);
		}
		throw new JsmIncidentManagementSetupError('timeout');
	};
	try {
		checkGate();
		onState({ stage: 'checking-organization' });
		await getOwningOrg(request);
		checkGate();
		onState({ stage: 'creating-team' });
		const { teamId } = await createTeam({
			...request,
			accountId: intent.accountId,
			displayName: INCIDENT_TEAM_NAME,
		});
		checkActive();
		onState({ stage: 'checking-membership', teamId });
		await confirmMembership(teamId);
		checkActive();
		onState({ stage: 'checking-eligibility', teamId });
		const eligibility = await getEligibility({ ...request, teamId, teamName: INCIDENT_TEAM_NAME });
		checkActive();
		if (eligibility.hasTeamDashboardRight) {
			onState({ stage: 'complete', teamId });
			return;
		}
		if (!eligibility.hasEnableOpsRight) throw new JsmIncidentManagementSetupError('permission');
		checkGate();
		onState({ stage: 'enabling', teamId });
		const { asyncRequestId } = await enableTeam({
			...request,
			teamId,
			adminAccountIds: [intent.accountId],
		});
		checkActive();
		onState({ stage: 'polling', teamId, asyncRequestId });
		await poll(teamId, asyncRequestId);
	} catch (error) {
		if (error instanceof BxpWebOperationsError)
			throw new JsmIncidentManagementSetupError('api', error);
		throw error;
	}
};
